import { useFormikContext } from "formik";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { AddressQcBanner } from "@/features/clients/components/address-qc-banner";
import type { Client } from "@/features/clients/types";
import {
  FormikCell,
  FormikCheckbox,
  FormikRadioGroup,
  FormikRadioYN,
  FormikSsnField,
  FormikTextarea,
} from "@/features/forms/components/formik-controls";
import type { Form8857Data } from "@/features/forms/types";
import { NoPractitionerBanner } from "./no-practitioner-banner";

const MARITAL_OPTIONS = [
  { value: "M", label: "Married and still living together" },
  { value: "A", label: "Married and living apart" },
  { value: "W", label: "Widowed" },
  { value: "S", label: "Legally separated" },
  { value: "D", label: "Divorced" },
];
const EDUCATION_OPTIONS = [
  { value: "N", label: "Did not complete high school" },
  { value: "D", label: "High school diploma or equivalent" },
  { value: "C", label: "Some college" },
  { value: "H", label: "College degree or higher" },
];
const SINCE_FIELD: Record<string, { name: keyof Form8857Data; hint: string }> = {
  A: { name: "married_apart_since", hint: "" },
  W: { name: "widowed_since", hint: "Attach a photocopy of the death certificate and will (if any)." },
  S: { name: "ligally_separate_since", hint: "Attach a photocopy of your entire separation agreement." },
  D: { name: "divorce_since", hint: "Attach a photocopy of your entire divorce decree." },
};

/**
 * Part II — Tell us about yourself and the person on line 6 (legacy
 * `sr_part2.php`). Language + conditional, the different-contact-address reveal,
 * the spouse/person block (SSN masked), marital status + conditional "since"
 * date, education + conditional degree, health, and afraid-to-provide. The
 * requesting person's own name/SSN + default address print live from the client
 * record (server-side), so they aren't re-typed here. Shows the incomplete-address
 * and no-practitioner nudges.
 */
export function SrPart2({ client, onNext }: { client: Client; onNext: () => void }) {
  const { values } = useFormikContext<Form8857Data>();
  const since = SINCE_FIELD[values.marital_status];

  return (
    <div className="flex flex-col gap-6">
      <AddressQcBanner warnings={client.addressQc ?? []} />
      <NoPractitionerBanner clientId={client.id} practitioner={client.practitioner} />

      <Card className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">4</span>
            Is English your primary or preferred language?
          </span>
          <FormikRadioYN name="prefer_english" aria-label="English is your preferred language" />
          {values.prefer_english === "N" && (
            <div className="grid gap-2 pt-2">
              <Label htmlFor="other_language">Primary or preferred language</Label>
              <FormikCell name="other_language" maxLength={50} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t pt-4">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">5</span>
            The requesting person's name, SSN, and default mailing address print
            from the client record. Send IRS mail to a different address?
          </span>
          <FormikCheckbox name="diff_add_chk" label="Use a different contact address" />
          {values.diff_add_chk && (
            <div className="grid gap-3 pt-2 sm:grid-cols-2">
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="diff_add1">Number and street or P.O. box</Label>
                <FormikCell name="diff_add1" maxLength={50} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="curr_spouce_apt">Apt. no.</Label>
                <FormikCell name="curr_spouce_apt" maxLength={20} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="curr_spouce_county">County</Label>
                <FormikCell name="curr_spouce_county" maxLength={20} />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="diff_add2">City, town or post office, state, and ZIP</Label>
                <FormikCell name="diff_add2" maxLength={80} />
              </div>
            </div>
          )}
          <div className="grid gap-3 pt-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="client_day_time_phone">Best daytime phone</Label>
              <FormikCell name="client_day_time_phone" maxLength={20} />
            </div>
            <div className="flex items-end">
              <FormikCheckbox name="irs_leave_voicemail" label="Consent to IRS voicemail at this number" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">6</span>
          The person you were married to for the tax years you want relief
        </span>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="curr_spouce">That person's current name</Label>
            <FormikCell name="curr_spouce" maxLength={40} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="curr_spouce_ssn">Social security number (if known)</Label>
            <FormikSsnField name="curr_spouce_ssn" aria-label="That person's SSN" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="curr_spouce_add1">Current home address (number and street)</Label>
            <FormikCell name="curr_spouce_add1" maxLength={50} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="curr_spouce_apt2">Apt. no.</Label>
            <FormikCell name="curr_spouce_apt2" maxLength={10} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="sp_day_time_phone">Daytime phone</Label>
            <FormikCell name="sp_day_time_phone" maxLength={20} />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="curr_spouce_add2">City, town or post office, state, and ZIP</Label>
            <FormikCell name="curr_spouce_add2" maxLength={50} />
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">7</span>
          Current marital status between you and the person on line 6
        </span>
        <FormikRadioGroup name="marital_status" options={MARITAL_OPTIONS} aria-label="Marital status" />
        {since && (
          <div className="grid gap-2 border-t pt-3">
            <Label htmlFor={since.name}>Since (MM/DD/YYYY)</Label>
            <FormikCell name={since.name} type="date" className="w-48" />
            {since.hint && <p className="text-muted-foreground text-xs">{since.hint}</p>}
          </div>
        )}
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">8</span>
          Highest level of education completed when the return(s) were filed
        </span>
        <FormikRadioGroup name="high_education" options={EDUCATION_OPTIONS} aria-label="Education level" />
        {values.high_education === "H" && (
          <div className="grid gap-2 border-t pt-3">
            <Label htmlFor="list_degree">List any degrees you have</Label>
            <FormikCell name="list_degree" maxLength={50} />
          </div>
        )}
        <div className="grid gap-2 border-t pt-3">
          <Label htmlFor="business_course">
            Any college-level business, tax, law, or accounting courses
          </Label>
          <FormikCell name="business_course" maxLength={100} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="education_explain">Explain (if answers differ across years)</Label>
          <FormikTextarea name="education_explain" rows={3} />
        </div>
      </Card>

      <Card className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">9</span>
            Did you have a mental or physical health problem when the returns were
            filed, or do you now?
          </span>
          <FormikRadioYN name="mental_physical_health" aria-label="Mental or physical health problem" />
          {values.mental_physical_health === "Y" && (
            <div className="grid gap-2 pt-2">
              <Label htmlFor="health_explain">Explain the problem and when it started</Label>
              <FormikTextarea name="health_explain" rows={3} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <span className="text-foreground text-sm">
            <span className="text-muted-foreground mr-2 font-mono">10</span>
            Any information you are afraid to provide, but willing to discuss?
          </span>
          <FormikRadioYN name="afraid_to_provide" aria-label="Afraid to provide information" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part III <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
